import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import Pager from "@/components/Pager";
import api from "@/lib/api";
import { devError } from "@/lib/logger";
import { getChartTheme } from "@/lib/chartTheme";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { KES, formatRelative } from "@/lib/format";
import {
  Activity, Users, Package, Router as RouterIcon, Wallet, Signal,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";

const TX_PAGE_SIZE = 8;

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatTick(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { theme } = useAdminTheme();
  const chart = getChartTheme(theme);

  const [from, setFrom] = useState(isoDaysAgo(13));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [revenueSeries, setRevenueSeries] = useState(null);

  const [txItems, setTxItems] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/admin/dashboard");
        setStats(data);
      } catch (e) {
        devError("[dashboard/load] failed", e?.message);
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/admin/dashboard/revenue-series", { params: { from, to } });
        setRevenueSeries(data.series);
      } catch (e) {
        devError("[dashboard/revenue-series] failed", e?.message);
      }
    };
    load();
  }, [from, to]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/admin/dashboard/recent-transactions", {
          params: { page: txPage, page_size: TX_PAGE_SIZE },
        });
        setTxItems(data.items); setTxTotal(data.total);
      } catch (e) {
        devError("[dashboard/recent-transactions] failed", e?.message);
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [txPage]);

  if (!stats) return <div className="p-8 text-nhp-muted">Loading operations data...</div>;

  return (
    <div>
      <PageHeader
        testId="dashboard-header"
        title="Operations Overview"
        subtitle="Live view of your entire hotspot network"
      />

      <div className="p-4 md:p-8 space-y-6 md:space-y-8">
        {/* Top metrics grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          <StatCard testId="stat-active-sessions" label="Active Sessions" value={stats.active_sessions} icon={Activity} />
          <StatCard testId="stat-revenue-today" label="Revenue Today" value={KES(stats.revenue_today)} icon={Wallet} accent="text-nhp-warning" />
          <StatCard testId="stat-revenue-month" label="Revenue MTD" value={KES(stats.revenue_month)} icon={Wallet} accent="text-nhp-primary" />
          <StatCard testId="stat-customers" label="Customers" value={stats.total_customers} icon={Users} accent="text-nhp-info" />
          <StatCard
            testId="stat-routers"
            label="Routers Online"
            value={`${stats.online_routers}/${stats.total_routers}`}
            icon={RouterIcon}
            accent={stats.online_routers < stats.total_routers ? "text-nhp-warning" : "text-nhp-primary"}
          />
          <StatCard testId="stat-packages" label="Active Packages" value={stats.total_packages} icon={Package} accent="text-nhp-text" />
        </div>

        {/* Chart + popular packages */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// REVENUE TREND</div>
                <div className="font-heading font-bold text-xl mt-1 flex items-center gap-2">
                  Revenue Trend <Signal className="h-4 w-4 text-nhp-primary" />
                </div>
              </div>
              <div className="flex items-center gap-2 font-mono text-xs">
                <input
                  type="date" data-testid="dashboard-revenue-from" value={from} max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-9 px-2 bg-nhp-bg border border-nhp-border text-nhp-text rounded-none"
                />
                <span className="text-nhp-muted">to</span>
                <input
                  type="date" data-testid="dashboard-revenue-to" value={to} min={from} max={isoDaysAgo(0)}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-9 px-2 bg-nhp-bg border border-nhp-border text-nhp-text rounded-none"
                />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueSeries || []}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chart.accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={chart.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatTick} tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <YAxis tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <Tooltip
                  contentStyle={chart.tooltip}
                  labelStyle={chart.tooltipLabel}
                  itemStyle={chart.tooltipItem}
                  labelFormatter={formatTick}
                  formatter={(v) => KES(v)}
                />
                <Area type="monotone" dataKey="revenue" stroke={chart.accent} strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// TOP PACKAGES</div>
            <div className="font-heading font-bold text-xl mt-1 mb-5">Popular</div>
            <div className="space-y-3">
              {stats.popular_packages.length === 0 && (
                <div className="text-sm text-nhp-muted">No purchases yet.</div>
              )}
              {stats.popular_packages.map((p, i) => (
                <div key={p.name || `pkg-${i}`} className="flex items-center justify-between border-b border-nhp-border pb-3 last:border-0">
                  <div>
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-nhp-muted font-mono">{p.count} txns</div>
                  </div>
                  <div className="font-mono font-bold text-nhp-primary tabular-nums">{KES(p.revenue)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent transactions */}
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="px-4 md:px-6 py-4 border-b border-nhp-border flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// LIVE FEED</div>
              <div className="font-heading font-bold text-lg md:text-xl mt-1">Recent Transactions</div>
            </div>
            <div className="font-mono text-xs text-nhp-primary flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-nhp-primary animate-pulse" />
              LIVE
            </div>
          </div>
          <div className="divide-y divide-nhp-border">
            {txItems.length === 0 && (
              <div className="p-6 text-sm text-nhp-muted">No recent transactions.</div>
            )}
            {txItems.map((tx) => (
              <div key={tx.id} className="px-4 md:px-6 py-3 flex flex-col gap-1 md:grid md:grid-cols-4 md:gap-4 md:items-center hover:bg-nhp-bg">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm">{tx.customer_phone}</div>
                  <div className="font-mono font-bold text-nhp-primary md:hidden tabular-nums">{KES(tx.amount_kes)}</div>
                </div>
                <div className="text-sm text-nhp-muted md:text-nhp-text">{tx.package_name}</div>
                <div className="font-mono text-sm text-nhp-muted">{formatRelative(tx.created_at)}</div>
                <div className="hidden md:block font-mono font-bold text-nhp-primary text-right tabular-nums">{KES(tx.amount_kes)}</div>
              </div>
            ))}
          </div>
          <Pager page={txPage} total={txTotal} pageSize={TX_PAGE_SIZE} itemLabel="transactions" onChange={setTxPage} testId="dashboard-tx" />
        </div>
      </div>
    </div>
  );
}
